import { ROUTES } from "../constants";
import { apiRequest } from "./api.client";
import type { DentalChart } from "../schemas/dental.schema";

export { ApiError as DiagnosisApiError } from "./api.errors";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface VitalsPayload {
  blood_pressure_systolic?: number;
  blood_pressure_diastolic?: number;
  heart_rate?: number;
  respiratory_rate?: number;
  temperature?: number;
  temperature_unit?: "Celsius" | "Fahrenheit";
  oxygen_saturation?: number;
  weight?: number;
  weight_unit?: "kg" | "lbs";
  height?: number;
  height_unit?: "cm" | "inches";
  head_circumference?: number;
  head_circumference_unit?: "cm" | "inches";
  blood_sugar?: number;
  bmi?: number;
}

export interface CreateVisitPayload {
  patient_id: string;
  doctor_id: string;
  visit_date: string;       // ISO datetime
  visit_type?: string;
  chief_complaint?: string;
  notes?: string;
  lab_tests?: string[];
  advice?: string;
  follow_up_date?: string;  // YYYY-MM-DD
  vitals?: VitalsPayload;
  /** Omit to leave the stored chart unchanged; an empty chart clears it. */
  dental_chart?: DentalChart;
}

/** GET /patients/{id}/billable-visits — a visit without its clinical content.
 *  chief_complaint is never sent; it is optional here only so the bill page
 *  can keep one label formatter. */
export type BillableVisit = Pick<
  VisitResponse,
  "visit_id" | "patient_id" | "doctor_id" | "visit_date" | "status" | "doctor_name" | "consultation_fee_paise"
> & { chief_complaint?: string };

export interface VisitResponse {
  visit_id: string;
  patient_id: string;
  doctor_id: string;
  visit_date: string;
  status: string;
  chief_complaint?: string;
  assessment?: string;
  notes?: string;
  lab_tests?: string[];
  advice?: string;
  follow_up_date?: string;
  dental_chart?: DentalChart | null;
  sync_version?: number;
  doctor_name?: string;
  consultation_fee_paise?: number;
}

export interface VisitWithVitalsResponse {
  visit: VisitResponse;
  vitals?: {
    vital_id: string;
    heart_rate?: number;
    blood_pressure_systolic?: number;
    blood_pressure_diastolic?: number;
    temperature?: number;
    oxygen_saturation?: number;
    respiratory_rate?: number;
    bmi?: number;
    blood_sugar?: number;
    height?: number;
    height_unit?: string;
    weight?: number;
    weight_unit?: string;
    head_circumference?: number;
    head_circumference_unit?: string;
  } | null;
}

export interface MedicationPayload {
  drug_id?: string;
  name: string;
  dosage: string;
  frequency: string;
  route?: string;
  duration?: string;
  quantity?: number;
  instructions?: string;
  timing?: string;
  override_reason?: string;
}

export interface CreatePrescriptionPayload {
  notes?: string;
  medications: MedicationPayload[];
  diagnosis_label?: string;
  investigations?: string[];
  advice?: string;
  follow_up?: string;
  ai_scribe_source?: boolean;
}

export interface MedicationResponse {
  medication_id: string;
  drug_id?: string;
  name: string;
  dosage: string;
  frequency: string;
  route?: string;
  duration?: string;
  quantity?: number;
  instructions?: string;
  timing?: string;
  override_reason?: string;
  ai_suggested?: boolean;
  allergy_conflict?: boolean;
}

// ── Review screen types ───────────────────────────────────────────────────────

export interface ReviewMedication {
  medication_id: string;
  drug_id?: string;
  name: string;
  dosage: string;
  frequency: string;
  timing?: string;
  route?: string;
  duration?: string;
  quantity?: number;
  instructions?: string;
  override_reason: string;
  ai_suggested: boolean;
  allergy_conflict: boolean;
  conflicting_allergen?: string;
}

export interface PrescriptionDocument {
  diagnosis_label: string;
  medications: Array<{
    name: string;
    strength: string;
    unit?: string;
    frequency: string;
    timing?: string;
    duration: number;
    quantity: number;
    instructions?: string;
    allergy_conflict: boolean;
  }>;
  investigations: string[];
  advice: string;
  follow_up?: string;
}

export interface PrescriptionResponse {
  prescription_id: string;
  visit_id: string;
  prescribed_by: string;
  prescribed_at: string;
  status: string;
  notes?: string;
  observation?: string;
  diagnosis_label?: string;
  investigations?: string[];
  advice?: string;
  follow_up?: string;
  medications: MedicationResponse[];
}

export interface PrescriptionPdfResponse {
  prescription_id: string;
  pdf_s3_key: string;
  pdf_url: string;           // presigned 24-hour GET URL
  generated_at: string;
}

export interface PatientInfo {
  patient_id: string;
  display_id?: string;
  patient_no?: number;
  first_name: string;
  last_name: string;
  date_of_birth: string;  // YYYY-MM-DD
  gender: string;
  contact_number?: string;
}

export interface VitalsTrendPoint {
  vital_id: string;
  visit_id: string;
  visit_date: string;
  blood_pressure_systolic?: number;
  blood_pressure_diastolic?: number;
  heart_rate?: number;
  respiratory_rate?: number;
  temperature?: number;
  temperature_unit?: string;
  oxygen_saturation?: number;
  weight?: number;
  weight_unit?: string;
  height?: number;
  height_unit?: string;
  head_circumference?: number;
  head_circumference_unit?: string;
  bmi?: number;
  blood_sugar?: number;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export const DiagnosisAPI = {
  createVisit(payload: CreateVisitPayload): Promise<VisitWithVitalsResponse> {
    return apiRequest<VisitWithVitalsResponse>(ROUTES.VISITS, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  patchVisit(
    visitId: string,
    payload: Partial<Omit<CreateVisitPayload, "patient_id" | "doctor_id" | "visit_date">>
  ): Promise<VisitResponse> {
    return apiRequest<VisitResponse>(ROUTES.VISIT(visitId), {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  updateVisitStep(visitId: string, step: "90sdiagnosis" | "soap-diagnosis-pad" | "continue-diagnosis"): Promise<void> {
    return apiRequest<void>(`/visits/${visitId}/step`, {
      method: "PATCH",
      body: JSON.stringify({ step }),
    });
  },

  getVisit(visitId: string): Promise<VisitWithVitalsResponse> {
    return apiRequest<VisitWithVitalsResponse>(ROUTES.VISIT(visitId));
  },

  finalizeVisit(visitId: string): Promise<VisitResponse> {
    return apiRequest<VisitResponse>(ROUTES.VISIT_FINALIZE(visitId), { method: "POST" });
  },

  createPrescription(visitId: string, payload: CreatePrescriptionPayload): Promise<PrescriptionResponse> {
    return apiRequest<PrescriptionResponse>(ROUTES.VISIT_PRESCRIPTIONS(visitId), {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  getPrescriptions(visitId: string): Promise<PrescriptionResponse[]> {
    return apiRequest<PrescriptionResponse[]>(ROUTES.VISIT_PRESCRIPTIONS(visitId));
  },

  updatePrescription(
    visitId: string,
    prescriptionId: string,
    payload: CreatePrescriptionPayload
  ): Promise<PrescriptionResponse> {
    return apiRequest<PrescriptionResponse>(ROUTES.VISIT_PRESCRIPTION(visitId, prescriptionId), {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  patchObservation(
    visitId: string,
    prescriptionId: string,
    observation: string
  ): Promise<PrescriptionResponse> {
    return apiRequest<PrescriptionResponse>(ROUTES.VISIT_PRESCRIPTION_OBS(visitId, prescriptionId), {
      method: "PATCH",
      body: JSON.stringify({ observation }),
    });
  },

  generatePdf(visitId: string, prescriptionId: string, pharmacyOnly = false): Promise<PrescriptionPdfResponse> {
    const url = ROUTES.VISIT_PRESCRIPTION_GENERATE_PDF(visitId, prescriptionId)
      + (pharmacyOnly ? "?pharmacy_only=true" : "");
    return apiRequest<PrescriptionPdfResponse>(url, { method: "POST" });
  },

  getPdfUrl(visitId: string, prescriptionId: string): Promise<PrescriptionPdfResponse> {
    return apiRequest<PrescriptionPdfResponse>(
      ROUTES.VISIT_PRESCRIPTION_PDF_URL(visitId, prescriptionId)
    );
  },

  getPatient(patientId: string): Promise<PatientInfo> {
    return apiRequest<PatientInfo>(ROUTES.PATIENT(patientId));
  },

  getVitalsTrend(patientId: string, limit = 90): Promise<VitalsTrendPoint[]> {
    return apiRequest<VitalsTrendPoint[]>(`${ROUTES.VITALS_TREND(patientId)}?limit=${limit}`);
  },

  getPatientVisits(patientId: string): Promise<VisitResponse[]> {
    return apiRequest<{ visits: VisitResponse[]; meta: unknown }>(ROUTES.PATIENT_VISITS(patientId))
      .then((r) => r.visits);
  },

  /** Billing fields only (billing.write) — for the Create Bill visit picker,
   *  which a receptionist must reach without the clinical visit history. */
  getBillableVisits(patientId: string): Promise<BillableVisit[]> {
    return apiRequest<BillableVisit[]>(ROUTES.PATIENT_BILLABLE_VISITS(patientId));
  },
};
